import { combine, sample } from 'effector'
import { $currentGroupId, $groupMap, IGroupMap } from '@entities/groups/model'
import { $goods } from '@entities/goods/model'
import {
    $previewSelectFiltersGoods,
    $selectedFiltersGoods,
    $selectedMakers,
    $selectedOutForms,
    IGoodFilterExt,
    resetGoodFilter
} from 'src/features/filter-goods/model/filter'
import { ICatalogGoodBase, IGoodFilter } from '@entities/goods/contract'
import { $currentPath, onRouteChangeStart } from '@entities/route/model'
import { useStoreMap } from 'effector-react'

const getCustomFilterId = (filter: IGoodFilter) => `${filter.siteFilterID}_${filter.filterValue}`

sample({
    clock: onRouteChangeStart,
    target: $currentGroupId.reinit!
})

function categoryFiltering({
    selectedGroupId,
    goods,
    groupMap
}: {
    selectedGroupId: number
    goods: ICatalogGoodBase[]
    groupMap: IGroupMap
}) {
    if (!selectedGroupId || selectedGroupId <= 0) return goods

    // берем товары только выделенной группы
    return goods.filter((good) => {
        return good.webData.goodGroups.some((gg) => {
            let currentGroup = groupMap[gg.clsGroupID]
            if (currentGroup.id === selectedGroupId) return true
            while (currentGroup.parentClsGroupId) {
                currentGroup = groupMap[currentGroup.parentClsGroupId]
                if (currentGroup.id === selectedGroupId) return true
            }
            return false
        })
    })
}

export function goodsFiltering(goods: ICatalogGoodBase[], filters: IGoodFilterExt[]) {
    if (goods && goods.length <= 0) {
        return []
    }
    if (!filters || filters.length <= 0) {
        return goods
    }
    let filteredGoods = goods
    const filterIds = [...new Map(filters.map((item) => [item.siteFilterID, item])).values()].map((x) => x.siteFilterID)

    filterIds.forEach((filterId) => {
        const currentFilters = filters.filter((x) => x.siteFilterID === filterId)
        filteredGoods = filteredGoods.filter(
            (x) => x.filters?.some((f) => currentFilters.some((sf) => sf.customId === getCustomFilterId(f))) || false
        )
    })
    return filteredGoods || []
}

export function goodsFilteringAndCategoryByPath({
    currentPath,
    selectedGroupId,
    selectedFiltersGoods,
    goods,
    groupMap,
    selectedOutForms,
    selectedMakers
}: {
    selectedGroupId: number
    currentPath: string
    selectedFiltersGoods: IGoodFilterExt[]
    goods: ICatalogGoodBase[]
    groupMap: IGroupMap
    selectedOutForms: string[]
    selectedMakers: string[]
}): ICatalogGoodBase[] {
    let filteredGoods = goods

    if (currentPath.includes('/search') || currentPath.includes('/catalog/suggestions')) {
        filteredGoods = categoryFiltering({ selectedGroupId, goods, groupMap })
    }
    filteredGoods = goodsFiltering(filteredGoods, selectedFiltersGoods)

    if (selectedOutForms && selectedOutForms.length > 0)
        filteredGoods = filteredGoods.filter((x) => selectedOutForms.indexOf(x.webData.outFormTitle) >= 0)

    if (selectedMakers && selectedMakers.length > 0)
        filteredGoods = filteredGoods.filter((x) => selectedMakers.indexOf(x.webData.makerTitle) >= 0)

    return filteredGoods
}

export const $goodsFiltered = combine(
    {
        currentPath: $currentPath,
        currentGroupId: $currentGroupId,
        selectedFiltersGoods: $selectedFiltersGoods,
        goods: $goods,
        groupMap: $groupMap,
        selectedOutForms: $selectedOutForms,
        selectedMakers: $selectedMakers
    },
    ({ currentPath, currentGroupId, selectedFiltersGoods, goods, groupMap, selectedOutForms, selectedMakers }) => {
        return goodsFilteringAndCategoryByPath({
            selectedGroupId: currentGroupId,
            currentPath,
            selectedFiltersGoods,
            goods,
            groupMap,
            selectedOutForms,
            selectedMakers
        })
    }
)
export const $previewGoodsFiltered = combine(
    {
        currentPath: $currentPath,
        currentGroupId: $currentGroupId,
        selectedFiltersGoods: $previewSelectFiltersGoods,
        goods: $goods,
        groupMap: $groupMap,
        selectedOutForms: $selectedOutForms,
        selectedMakers: $selectedMakers
    },
    ({ currentPath, currentGroupId, selectedFiltersGoods, goods, groupMap, selectedOutForms, selectedMakers }) =>
        goodsFilteringAndCategoryByPath({
            selectedGroupId: currentGroupId,
            currentPath,
            selectedFiltersGoods,
            goods,
            groupMap,
            selectedOutForms,
            selectedMakers
        })
)

export const useGoodsFilteredIsEmpty = () => useStoreMap($goodsFiltered, (goods) => goods.length <= 0)
export const usePreviewGoodsFilteredCount = () => useStoreMap($previewGoodsFiltered, (goods) => goods.length)
